import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { trackCompleteRegistration } from "@/lib/pixel";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Auth({ mode = "login" }) {
  const isLogin = mode === "login";
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isLogin) {
        await login(email, password);
        toast.success("Bentornato!");
        navigate("/dashboard");
      } else {
        await register(name, email, password);
        trackCompleteRegistration({ method: "email" });
        toast.success("Account creato. Iniziamo dal questionario!");
        // Create a fresh book and jump straight into its questionnaire so the new author has nothing to figure out.
        try {
          const { data: book } = await api.post("/books", {});
          navigate(`/books/${book.book_id}/questionario`);
        } catch {
          // Fallback: if book creation fails for any reason, still land in the dashboard.
          navigate("/dashboard");
        }
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-ink lg:grid-cols-2">
      {/* Editorial panel */}
      <div className="relative hidden overflow-hidden border-r border-white/5 lg:block">
        <img
          src="https://images.unsplash.com/photo-1772987273687-4866382c146f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Autrice"
          className="h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050A15] via-[#050A15]/50 to-transparent" />
        <div className="absolute bottom-0 left-0 p-12">
          <p className="font-serif text-4xl font-light leading-tight text-white">
            Il tuo libro,<br />
            <span className="italic text-[#D4AF37]">dall'idea ad Amazon.</span>
          </p>
          <p className="mt-4 max-w-sm text-white/50">
            Una sola piattaforma. Nessuna email, nessun PDF.
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-20">
        <div className="mx-auto w-full max-w-md">
          <Logo className="mb-12" />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <h1 className="font-serif text-4xl font-light text-white">
              {isLogin ? "Bentornato" : "Crea il tuo account"}
            </h1>
            <p className="mt-2 text-white/50">
              {isLogin ? "Accedi per continuare il tuo progetto." : "Inizia il tuo percorso editoriale in un minuto."}
            </p>

            <Button
              type="button"
              onClick={googleLogin}
              data-testid="google-login-btn"
              variant="outline"
              className="mt-8 w-full gap-3 border-white/10 bg-white/5 text-white hover:bg-white/10"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24">
                <path fill="#EA4335" d="M12 5c1.6 0 3 .5 4.1 1.6L19 3.7C17.1 1.9 14.7 1 12 1 7.3 1 3.3 3.7 1.4 7.6l3.3 2.6C5.6 7.3 8.5 5 12 5z" />
                <path fill="#4285F4" d="M23 12.2c0-.8-.1-1.4-.2-2.1H12v4.1h6.2c-.1 1-.8 2.5-2.2 3.5l3.4 2.6c2-1.9 3.1-4.7 3.1-8.1z" />
                <path fill="#FBBC05" d="M4.7 14.2c-.2-.6-.4-1.4-.4-2.2s.1-1.5.4-2.2L1.4 7.2C.6 8.7.2 10.3.2 12s.4 3.3 1.2 4.8l3.3-2.6z" />
                <path fill="#34A853" d="M12 23c2.7 0 5-.9 6.7-2.5l-3.4-2.6c-.9.6-2 1-3.3 1-3.5 0-6.4-2.3-7.3-5.5l-3.3 2.6C3.3 20.3 7.3 23 12 23z" />
              </svg>
              Continua con Google
            </Button>

            <div className="my-6 flex items-center gap-4">
              <span className="h-px flex-1 bg-white/10" />
              <span className="text-xs uppercase tracking-widest text-white/30">oppure</span>
              <span className="h-px flex-1 bg-white/10" />
            </div>

            <form onSubmit={submit} className="flex flex-col gap-4">
              {!isLogin && (
                <div>
                  <Label className="text-white/70">Nome</Label>
                  <Input
                    data-testid="auth-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    placeholder="Il tuo nome"
                    className="mt-1.5 border-white/10 bg-white/5 text-white placeholder:text-white/30"
                  />
                </div>
              )}
              <div>
                <Label className="text-white/70">Email</Label>
                <Input
                  data-testid="auth-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="nome@email.it"
                  className="mt-1.5 border-white/10 bg-white/5 text-white placeholder:text-white/30"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-white/70">Password</Label>
                  {isLogin && (
                    <Link to="/forgot-password" data-testid="forgot-password-link"
                      className="text-xs text-[#D4AF37] hover:underline">
                      Password dimenticata?
                    </Link>
                  )}
                </div>
                <Input
                  data-testid="auth-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder="••••••••"
                  className="mt-1.5 border-white/10 bg-white/5 text-white placeholder:text-white/30"
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                data-testid="auth-submit-btn"
                className="group mt-2 h-11 rounded-full bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]"
              >
                {busy ? "Attendi…" : isLogin ? "Accedi" : "Crea account"}
                {!busy && <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />}
              </Button>
            </form>

            <p className="mt-8 text-center text-sm text-white/50">
              {isLogin ? "Non hai un account? " : "Hai già un account? "}
              <Link
                to={isLogin ? "/register" : "/login"}
                data-testid="auth-switch-link"
                className="text-[#D4AF37] hover:underline"
              >
                {isLogin ? "Registrati" : "Accedi"}
              </Link>
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
